// src/pages/admin/ProveedoresPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { createProvider, getSuppliers, getSupplierTypes, SupplierType, type Proveedor } from "../../services/api";

const ProveedoresPage: React.FC = () => {
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [nombre, setNombre] = useState("");
  const [codigo, setCodigo] = useState("");
  const [supplierTypeId, setSupplierTypeId] = useState("");
  const [supplierTypes, setSupplierTypes] = useState<SupplierType[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    fetchProveedores();
  }, []);

  // 1️⃣ Obtener proveedores
    const fetchProveedores = async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const data = await getSuppliers();
        setProveedores(data);
        const st = await getSupplierTypes();
        setSupplierTypes(st);
      } catch (err: any) {
        const status = err?.response?.status ?? "??";
        const msg = err?.response?.data?.message || err?.message || "Error";
        setErrorMsg(`❌ No se pudieron cargar los proveedores (${status}): ${msg}`);
        console.error("Suppliers error:", err?.response || err);
      } finally {
        setLoading(false);
      }
    };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return proveedores;
    return proveedores.filter((p) => {
      const nom = (p.nombre || "").toLowerCase();
      const cod = (p.codigo || "").toLowerCase();
      const tipo = (p.tipo || "").toLowerCase();
      return nom.includes(q) || cod.includes(q) || tipo.includes(q) || String(p.id).includes(q);
    });
  }, [proveedores, query]);

  const initials = (name?: string) =>
    (name || "—")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("") || "—";

  const fmt = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "2-digit" }) : "—";

  const agregarProveedor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim() || !codigo.trim()) return;

    setErrorMsg(null);
    setLoadingAction(true);
    try {
      await createProvider({
        code: codigo.trim(), 
        name: nombre.trim(), 
        supplierTypeId: Number(supplierTypeId),
        version: 0
      });
      setNombre("");
      setCodigo("");
      setSupplierTypeId("");
      await fetchProveedores();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || "Error";
      setErrorMsg(`❌ Error al agregar proveedor (${status ?? "??"}): ${msg}`);
      console.error(err);
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <section className="relative overflow-hidden rounded-3xl shadow-lg ring-1 ring-slate-200">
          <div className="absolute inset-0 bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700" />
          <div className="pointer-events-none absolute -top-16 -left-16 h-48 w-48 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-white/10 blur-3xl" />

          <div className="relative p-6 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-3xl font-extrabold text-white drop-shadow-sm">Proveedores</h2>
                {false && (<p className="mt-1 text-sky-100/95">
                  Vista de <b>solo lectura</b>. Cuando el backend permita crear/editar/eliminar,
                  activaremos esas acciones aquí.
                </p>)}
              </div>
              <div className="inline-flex items-center gap-2 rounded-xl bg-black/20 px-3 py-2 text-white shadow-inner">
                <span className="text-sm opacity-90">Total</span>
                <span className="text-lg font-bold">{proveedores.length}</span>
              </div>
            </div>

            {/* Buscador */}
            <div className="mt-5">
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-200">🔎</span>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar por nombre, código, tipo o ID…"
                  className="w-full rounded-xl border border-white/20 bg-white/95 pl-9 pr-3 py-2 text-slate-800 placeholder-slate-500 shadow focus:outline-none focus:ring-2 focus:ring-sky-300"
                />
              </div>
            </div>

            {/* Aviso */}
            {false && (<div className="mt-4 rounded-lg border border-amber-300/50 bg-amber-50/95 p-3 text-amber-900">
              ℹ️ El backend aún no expone endpoints para <b>crear/editar/eliminar proveedores</b>.
            </div>)}
<br></br>
        {/* Card del formulario */}
        <section
          className="relative mx-auto max-w-xl overflow-hidden rounded-2xl bg-white/80 p-6 backdrop-blur ring-1 ring-slate-200 shadow-sm"
          aria-labelledby="form-title"
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600" />
          <h3 id="form-title" className="sr-only">Formulario de proveedor</h3>

          <form onSubmit={agregarProveedor} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-800">
                Código
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                required
                disabled={loadingAction}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-800">
                Nombre del proveedor
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                disabled={loadingAction}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Tipo de proveedor
              </label>
              <select
                value={supplierTypeId}
                onChange={(e) => setSupplierTypeId(e.target.value)}
                required
                disabled={loading || loadingAction}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 outline-none"
              >
                <option value="">-- Seleccionar Tipo de proveedor --</option>
                {supplierTypes.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              className="w-full rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 font-semibold text-white shadow transition hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={loadingAction}
            >
              {loadingAction ? "Guardando…" : "➕ Guardar Proveedor"}
            </button>
          </form>
        </section>
          </div>
        </section>
      </header>

      {/* Contenido */}
      <main className="mx-auto max-w-6xl px-4 mt-6">
        {errorMsg && (
          <div className="mb-4 rounded-lg border border-red-300/60 bg-red-50 px-4 py-3 text-red-900">
            {errorMsg}
          </div>
        )}

        {loading && (
          <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className="h-32 rounded-2xl bg-white/70 backdrop-blur-sm animate-pulse ring-1 ring-slate-200" />
            ))}
          </ul>
        )}

        {!loading && (
          <>
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white/95 p-6 text-slate-600">
                No hay proveedores que coincidan con tu búsqueda.
              </div>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map((p) => (
                  <li
                    key={p.id}
                    className="group relative overflow-hidden rounded-2xl bg-white/95 p-5 shadow transition hover:-translate-y-0.5 hover:shadow-lg ring-1 ring-slate-200"
                  >
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-700" />

                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700 text-white font-bold shadow-sm">
                        {initials(p.nombre)}
                      </div>

                      <div className="min-w-0 flex-1">
                        {/* Badges */}
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">
                            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                            ID: {p.id}
                          </span>

                          {p.codigo && (
                            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-200">
                              Código: {p.codigo}
                            </span>
                          )}

                          {p.tipo && (
                            <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700 ring-1 ring-sky-200">
                              {p.tipo}
                            </span>
                          )}

                          {typeof p.activo === "boolean" && (
                            <span
                              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${
                                p.activo
                                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                  : "bg-rose-50 text-rose-700 ring-rose-200"
                              }`}
                            >
                              {p.activo ? "Activo" : "Inactivo"}
                            </span>
                          )}
                        </div>

                        {/* Nombre */}
                        <h3 className="mt-2 truncate text-lg font-bold text-slate-900">{p.nombre || "—"}</h3>

                        {/* Fechas */}
                        <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                          <span>Creado: {fmt(p.creado)}</span>
                          <span>Actualizado: {fmt(p.actualizado)}</span>
                          {p.organizationId && <span>Org: {p.organizationId}</span>}
                        </div>

                        <p className="mt-2 text-xs text-slate-500">
                          (Acciones de edición/eliminación aún no disponibles)
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ProveedoresPage;
